import { waitFor, DeferredUpdater, LRUList, profiler } from '@dinosband/dbi-utils'

interface KeySeq<K> {
  key: K
  seq: number
}

interface MemCacheItem<T> {
  seq: number
  data?: T // data == undefined means this item is to be deleted
  seqChanged?: boolean
  dataChanged?: boolean
}

//--------------------------------------------------------------------------
// DataCache
//--------------------------------------------------------------------------
export abstract class DataCacheBase<K extends number | string, T> {
  protected name: string
  protected index = new LRUList<K, number>()
  protected memCache = new LRUList<K, MemCacheItem<T>>()

  protected maxCount: number
  protected maxMemCacheCount: number
  protected seq = 1

  protected initializing = false
  protected initialized = false

  protected updater: DeferredUpdater

  protected abstract _initialize (): Promise<KeySeq<K>[]>
  protected abstract _reset (): Promise<void>
  protected abstract _get (key: K): Promise<T | null>
  protected abstract _set (key: K, seq: number, item: T): Promise<void>
  protected abstract _touch (key: K, seq: number): Promise<void>
  protected abstract _delete (key: K): Promise<void>

  constructor (name: string, maxCount: number, maxMemCacheCount: number, updateDelay = 3000) {
    this.name = name
    this.maxCount = maxCount
    this.maxMemCacheCount = maxMemCacheCount

    this.updater = new DeferredUpdater(async () => {
      await this.flush()
    }, updateDelay)
  }

  get minKey () {
    return this.index.minKey
  }

  get maxKey () {
    return this.index.maxKey
  }

  async initialize () {
    if (this.initialized) {
      return
    }

    if (this.initializing) {
      await waitFor(() => !this.initializing)
      return
    }

    // not initialized nor initializing
    this.initializing = true
    const indexes = await this._initialize()
    if (indexes.length > 0) {
      indexes.sort((a, b) => a.seq - b.seq)
      indexes.forEach((ind) => {
        this.index.set(ind.key, ind.seq)
      })
      this.seq = indexes[indexes.length - 1].seq + 1
    }
    this.initializing = false

    this.initialized = true
  }

  async reset (): Promise<void> {
    await this.initialize()
    this.index.reset()
    this.memCache.reset()
    await this._reset()
  }

  async get (key: K): Promise<T | null> {
    await this.initialize()
    const item = this.memCache.get(key)
    if (item) {
      if (item.data) {
        item.seq = this.seq++
        item.seqChanged = true
        this.memCache.set(key, item)
        this.index.touch(key)
        this.updater.touch()
        return item.data
      }
    } else {
      if (this.index.get(key)) {
        profiler.of(this.name + '.get').begin()
        const data = await this._get(key)
        profiler.of(this.name + '.get').end()
        if (data) {
          this.memCache.set(key, {
            seq: this.seq++,
            seqChanged: true,
            data,
          })
          this.updater.touch()
          // this.checkMemCacheCount()
          return data
        }
      }
    }
    return null
  }

  async set (key: K, data: T): Promise<void> {
    await this.initialize()
    // Since the size is adjusted in flush(), there is no need to check here anymore.
    /*
    if (this.maxCount > 0) {
      if (!this.index.get(key)) {
        // new data
        let count = this.index.count + 1
        for (; this.maxCount < count; count--) {
          const deletedKey = this.index.deleteOldest()
          if (deletedKey) {
            this.deleted.add(deletedKey)
          }
        }
      }
    }
    */

    this.memCache.set(key, {
      seq: this.seq++,
      data,
      dataChanged: true,
    })
    this.updater.touch()
    // this.checkMemCacheCount()
  }

  async delete (key: K): Promise<void> {
    await this.initialize()
    const item = this.memCache.get(key)
    if (item) {
      item.data = undefined
    } else {
      // if the item to be deleted is not in memCache but is in index,
      // create empty item in memCache marked deleted, so the item is deleted in flush().
      if (this.index.get(key)) {
        this.memCache.set(key, {
          seq: 0,
          // undefined data
        })
      }
    }
    this.updater.touch()
  }

  protected async checkMemCacheCount (): Promise<void> {
    // keep memCache count <= maxMemCacheCount
    while (this.maxMemCacheCount < this.memCache.count) {
      const item = this.memCache.getOldest()
      const key = this.memCache.deleteOldest()
      if (item && key) {
        if (item.dataChanged && item.data) {
          await this._set(key, item.seq, item.data)
        } else if (item.seqChanged) {
          await this._touch(key, item.seq)
        }
      }
    }
  }

  async flush (): Promise<void> {
    profiler.of(this.name + '.flush').begin()
    // find modified items
    const modified: { key: K, item: MemCacheItem<T> }[] = []
    this.memCache.forEach(async (key, value) => {
      const item = value as MemCacheItem<T>
      if (item.seqChanged || item.dataChanged || !item.data) {
        modified.push({ key, item })
      }
    })
    // if (this.name.includes('thumbnail')) {
    // }

    // apply modified items
    for (const m of modified) {
      const item = m.item
      if (!item.data) {
        this.memCache.delete(m.key)
        this.index.delete(m.key)
        await this._delete(m.key)
      } else {
        if (item.dataChanged) {
          this.index.set(m.key, item.seq)
          await this._set(m.key, item.seq, item.data)
        } else if (item.seqChanged) {
          this.index.set(m.key, item.seq)
          await this._touch(m.key, item.seq)
        }
        item.dataChanged = false
        item.seqChanged = false
      }
    }

    // adjust size of cache
    while (this.maxCount < this.index.count) {
      const key = this.index.deleteOldest()
      if (key) {
        await this._delete(key)
      }
    }

    // adjust size of memCache
    while (this.maxMemCacheCount < this.memCache.count) {
      this.memCache.deleteOldest()
    }
    profiler.of(this.name + '.flush').end()
  }
}
